import { Box, CircularProgress, Container, Typography } from '@mui/material';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate } from 'react-router-dom';
import WorkspaceLogo from '~/components/Logo/WorkspaceLogo';
import { checkIsInSessionRouteWithoutRouter } from '~/hooks/session/useIsInSessionRoute';
import { useAccessRights } from '~/hooks/workflows/backend/useBackend';
import AppConfig from '~/utils/appconfig';

/**
 * ProtectedRoute component that handles access rights validation.
 *
 * This component assumes the user is already authenticated (handled by AuthenticationGuard)
 * and focuses only on access rights validation.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const isInConnectorRoute = checkIsInSessionRouteWithoutRouter();

    const shouldSkipAccessCheck =
        AppConfig.accessRightKey === 'development' ||
        isInConnectorRoute ||
        !AppConfig.authority;

    const {
        data: hasAccess,
        isLoading,
        error,
    } = useAccessRights(!shouldSkipAccessCheck);

    if (shouldSkipAccessCheck) {
        return <>{children}</>;
    }

    if (isLoading) {
        return (
            <Container maxWidth="sm">
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="100vh"
                    textAlign="center"
                    gap={4}
                >
                    <WorkspaceLogo height={120} />
                    <CircularProgress size={40} />
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        <FormattedMessage
                            id="checkingAccessRights"
                            defaultMessage="Checking access rights..."
                        />
                    </Typography>

                    <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 4, opacity: 0.7 }}
                    >
                        <FormattedMessage
                            id="versionInfo"
                            defaultMessage="Version {version}"
                            values={{ version: AppConfig.version }}
                        />
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (error || !hasAccess) {
        return <Navigate to="/access-denied" />;
    }

    return <>{children}</>;
}

export default ProtectedRoute;
